const initMemberDashboard = (dashboard) => {
  const links = [...dashboard.querySelectorAll('[data-mm-dashboard-link]')];
  const views = [...dashboard.querySelectorAll('[data-mm-dashboard-view]')];
  const statusNode = dashboard.querySelector('[data-mm-status]');

  if (!links.length || !views.length) return;

  const showView = (name, focusHeading) => {
    views.forEach((view) => {
      view.hidden = view.dataset.mmDashboardView !== name;
    });

    links.forEach((link) => {
      const active = link.dataset.mmDashboardLink === name;
      link.classList.toggle('is-active', active);

      // The sidebar is a real nav, so the active item needs to be announced
      // and not merely coloured. The overview shortcut cards stay unmarked.
      if (!link.closest('.mm-dashboard__nav')) return;
      if (active) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });

    // A status message belongs to the action that produced it, not to the whole
    // dashboard: clear it when the customer moves to another view.
    if (statusNode) {
      statusNode.textContent = '';
      statusNode.hidden = true;
    }

    if (!focusHeading) return;

    // Move focus into the view that was just opened: a screen reader hears the
    // heading instead of silence, and on mobile (where the whole nav sits above
    // the content) the view is scrolled up instead of changing off-screen.
    const view = views.find((item) => item.dataset.mmDashboardView === name);
    const heading = view && view.querySelector('h2, h3');
    if (!heading) return;

    if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });

    if (heading.getBoundingClientRect().top > window.innerHeight * 0.4) {
      heading.scrollIntoView({ block: 'start' });
    }
  };

  links.forEach((link) => {
    link.addEventListener('click', (event) => {
      const name = link.dataset.mmDashboardLink;
      if (!name) return;

      event.preventDefault();
      showView(name, true);
      // batch 244: one history entry per view, so Back/Forward walk between the dashboard
      // views instead of leaving the page.
      if (window.location.hash.slice(1) !== name) history.pushState(null, '', `#${name}`);
    });
  });

  // batch 244: Back/Forward land on the view that entry was pushed from.
  window.addEventListener('popstate', () => {
    const name = window.location.hash.slice(1);
    showView(views.some((view) => view.dataset.mmDashboardView === name) ? name : 'overview', false);
  });

  const initial = window.location.hash.slice(1);
  showView(views.some((view) => view.dataset.mmDashboardView === initial) ? initial : 'overview', false);
};

document.querySelectorAll('[data-mm-dashboard]').forEach(initMemberDashboard);

// The Shopify account dialog owns classic authentication; its email field lives
// in an open shadow root. Copy the address the customer already typed so nobody
// has to type it twice. This stays as the fallback for shops where the Customer
// Account API client is not configured.
const findAccountEmailField = (root, depth = 0, seen = new Set()) => {
  if (!root || depth > 8 || seen.has(root)) return null;
  seen.add(root);

  const direct = root.querySelector ? root.querySelector('input[type="email"]') : null;
  if (direct) return direct;

  for (const element of root.querySelectorAll('*')) {
    if (!element.shadowRoot) continue;
    const found = findAccountEmailField(element.shadowRoot, depth + 1, seen);
    if (found) return found;
  }

  return null;
};

const carryEmailToAccountDialog = (account, email) => {
  let attempts = 0;

  const fill = () => {
    attempts += 1;

    const field = account && account.shadowRoot ? findAccountEmailField(account.shadowRoot) : null;
    if (!field) {
      if (attempts <= 8) window.setTimeout(fill, 250);
      return;
    }

    if (field.value) return;

    const setValue = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    field.focus();
    setValue.call(field, email);
    field.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
    field.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
  };

  window.setTimeout(fill, 0);
};

// ---------------------------------------------------------------------------
// Customer Account API (headless storefront client, PKCE public flow).
// Sign-in happens on the shop hosted page; the account itself is rendered here,
// so members never land on the Shopify-hosted account pages.
// ---------------------------------------------------------------------------

const entryConfig = (() => {
  const node = document.querySelector('[data-mm-entry-config]');
  if (!node) return null;

  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    return null;
  }
})();

const ACCOUNT_KEY = 'monsga-customer-account';
const PKCE_KEY = 'monsga-customer-account-pkce';
const accountStrings = (entryConfig && entryConfig.i18n) || {};
const returnStatusLabels = accountStrings.returnStatus || {};

// Payment and fulfilment are separate dimensions: an order can be paid but not
// shipped, and a voided payment is not a paid one. The card chips and the order
// filters both read these helpers, so a badge can never contradict a filter.
const PAYMENT_FILTERS = {
  paid: ['PAID', 'PARTIALLY_PAID'],
  pending: ['PENDING', 'AUTHORIZED'],
  refunded: ['REFUNDED', 'PARTIALLY_REFUNDED'],
  voided: ['VOIDED'],
};

const orderPaymentLabel = (status) => {
  const key = String(status || '').toUpperCase();
  const labels = {
    PAID: accountStrings.paymentPaid,
    PARTIALLY_PAID: accountStrings.paymentPaid,
    PENDING: accountStrings.paymentPending,
    AUTHORIZED: accountStrings.paymentPending,
    REFUNDED: accountStrings.paymentRefunded,
    PARTIALLY_REFUNDED: accountStrings.paymentRefunded,
    VOIDED: accountStrings.paymentVoided,
  };
  return labels[key] || humanizeStatus(status);
};

// The customer API reports fulfilment per fulfilment, not per line item, so a
// "partially shipped" state cannot be derived from it. Shipped means at least one
// fulfilment that was not cancelled; anything else has not shipped yet. Never
// "delivered": nothing in the payload says the parcel reached the door.
const orderFulfillmentState = (order) => {
  const nodes = ((order && order.fulfillments) || {}).nodes || [];
  const live = nodes.filter((item) => item && String(item.status || '').toUpperCase() !== 'CANCELLED');
  return live.length ? 'shipped' : 'unshipped';
};

const orderFulfillmentLabel = (state) => (state === 'shipped'
  ? (accountStrings.fulfillmentShipped || '')
  : (accountStrings.fulfillmentUnshipped || ''));

// Order numbers are typed by people: drop a leading #, ignore case and any
// surrounding blanks, then match on a continuous fragment.
const normalizeOrderTerm = (value) => String(value == null ? '' : value).trim().replace(/^#+/, '').trim().toLowerCase();

const orderMatchesFilters = (order, filters) => {
  const payment = String(order.financialStatus || '').toUpperCase();
  if (filters.payment && (PAYMENT_FILTERS[filters.payment] || []).indexOf(payment) === -1) return false;
  if (filters.fulfillment && orderFulfillmentState(order) !== filters.fulfillment) return false;
  if (filters.term && normalizeOrderTerm(order.name).indexOf(filters.term) === -1) return false;
  return true;
};

const readAccount = () => {
  try {
    return JSON.parse(window.localStorage.getItem(ACCOUNT_KEY) || 'null');
  } catch (error) {
    return null;
  }
};

const writeAccount = (account) => {
  try {
    window.localStorage.setItem(ACCOUNT_KEY, JSON.stringify(account));
  } catch (error) {
    // Private browsing: the session simply will not survive a reload.
  }
};

const clearAccount = () => {
  try {
    window.localStorage.removeItem(ACCOUNT_KEY);
  } catch (error) {
    // Nothing to clear.
  }
};

const base64Url = (bytes) =>
  window
    .btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const randomValue = (size = 48) => base64Url(window.crypto.getRandomValues(new Uint8Array(size)));

const sha256 = async (value) =>
  base64Url(new Uint8Array(await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value))));

const escapeHtml = (value) =>
  String(value == null ? '' : value).replace(
    /[&<>"']/g,
    (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]
  );

const redirectUri = () => {
  const url = new URL(window.location.href);
  const preview = url.searchParams.get('preview_theme_id');
  return `${url.origin}${url.pathname}${preview ? `?preview_theme_id=${preview}` : ''}`;
};

const requestTokens = async (body) => {
  const response = await fetch(entryConfig.tokenEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body),
  });

  if (!response.ok) return null;

  const data = await response.json();
  if (!data.access_token) return null;

  return {
    accessToken: data.access_token,
    idToken: data.id_token || '',
    refreshToken: data.refresh_token || '',
    expiresAt: Date.now() + (Number(data.expires_in) || 3600) * 1000 - 60000,
  };
};

const startSignIn = async (email) => {
  const verifier = randomValue();
  const state = randomValue(12);
  const redirect = redirectUri();

  try {
    window.sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, redirect }));
  } catch (error) {
    // Without storage the round trip cannot be verified, so signing in will fail.
  }

  const url = new URL(entryConfig.authorizeEndpoint);
  url.searchParams.set('scope', entryConfig.scope);
  url.searchParams.set('client_id', entryConfig.clientId);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('redirect_uri', redirect);
  url.searchParams.set('state', state);
  url.searchParams.set('nonce', randomValue(12));
  url.searchParams.set('code_challenge', await sha256(verifier));
  url.searchParams.set('code_challenge_method', 'S256');
  if (email) url.searchParams.set('login_hint', email);

  window.location.assign(url.toString());
};

const finishSignIn = async () => {
  const url = new URL(window.location.href);
  const code = url.searchParams.get('code');

  if (!code) return readAccount();

  const state = url.searchParams.get('state');
  ['code', 'state', 'error', 'error_description'].forEach((key) => url.searchParams.delete(key));
  window.history.replaceState(null, '', url.toString());

  let pending = null;
  try {
    pending = JSON.parse(window.sessionStorage.getItem(PKCE_KEY) || 'null');
    window.sessionStorage.removeItem(PKCE_KEY);
  } catch (error) {
    // Treated as a missing verifier below.
  }

  if (!pending || pending.state !== state) return null;

  const account = await requestTokens({
    grant_type: 'authorization_code',
    client_id: entryConfig.clientId,
    redirect_uri: pending.redirect,
    code,
    code_verifier: pending.verifier,
  });

  if (account) writeAccount(account);
  return account;
};

const activeAccount = async () => {
  const account = readAccount();
  if (!account) return null;
  if (account.accessToken && Date.now() < account.expiresAt) return account;

  if (!account.refreshToken) {
    clearAccount();
    return null;
  }

  const renewed = await requestTokens({
    grant_type: 'refresh_token',
    client_id: entryConfig.clientId,
    refresh_token: account.refreshToken,
  });

  if (!renewed) {
    clearAccount();
    return null;
  }

  const merged = {
    ...renewed,
    idToken: renewed.idToken || account.idToken,
    refreshToken: renewed.refreshToken || account.refreshToken,
  };
  writeAccount(merged);
  return merged;
};

const accountQuery = async (query, variables) => {
  const account = await activeAccount();
  if (!account) return { unauthorized: true };

  const response = await fetch(entryConfig.graphqlEndpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: account.accessToken },
    body: JSON.stringify({ query, variables }),
  });

  if (response.status === 401) {
    clearAccount();
    return { unauthorized: true };
  }

  return response.json().catch(() => ({}));
};

const ORDER_FIELDS = `
        id
        name
        processedAt
        totalPrice { amount currencyCode }
        subtotal { amount currencyCode }
        totalShipping { amount currencyCode }
        totalTax { amount currencyCode }
        totalRefunded { amount currencyCode }
        financialStatus
        statusPageUrl
        billingAddress { formatted(withName: true) }
        shippingAddress { formatted(withName: true) }
        lineItems(first: 50) { nodes { name quantity image { url altText } } }
        fulfillments(first: 10) {
          nodes {
            id
            status
            createdAt
            trackingInformation { company number url }
          }
        }
        returnInformation {
          returnableLineItems(first: 50) {
            nodes {
              quantity
              lineItem {
                id
                name
                suggestedReturnReasonDefinitions(first: 10) { nodes { id name } }
              }
            }
          }
        }
        returns(first: 10) { nodes { id name createdAt status } }
`;
const COUPON_QUERY = 'query MonsgaCoupons { customer { metafields(identifiers: [{ namespace: "custom", key: "monsga_coupons" }]) { key value } } }';

const ACCOUNT_QUERY = `query MonsgaAccount {
  customer {
    id
    displayName
    firstName
    lastName
    emailAddress { emailAddress marketingState }
    phoneNumber { phoneNumber }
    defaultAddress { id }
    addresses(first: 20) {
      nodes {
        id
        firstName
        lastName
        address1
        address2
        city
        territoryCode
        zoneCode
        zip
        phoneNumber
        formatted(withName: true)
      }
    }
    orders(first: 20, reverse: true) {
      pageInfo { hasNextPage endCursor }
      nodes {${ORDER_FIELDS}
      }
    }
  }
}`;

const ORDERS_PAGE_QUERY = `query MonsgaOrders($after: String) {
  customer {
    orders(first: 20, after: $after, reverse: true) {
      pageInfo { hasNextPage endCursor }
      nodes {${ORDER_FIELDS}
      }
    }
  }
}`;

const ADDRESS_MUTATIONS = {
  create: `mutation MonsgaAddressCreate($address: CustomerAddressInput!, $defaultAddress: Boolean) {
    customerAddressCreate(address: $address, defaultAddress: $defaultAddress) { userErrors { field message } }
  }`,
  update: `mutation MonsgaAddressUpdate($address: CustomerAddressInput, $addressId: ID!, $defaultAddress: Boolean) {
    customerAddressUpdate(address: $address, addressId: $addressId, defaultAddress: $defaultAddress) { userErrors { field message } }
  }`,
  remove: `mutation MonsgaAddressDelete($addressId: ID!) {
    customerAddressDelete(addressId: $addressId) { userErrors { field message } }
  }`,
};

const RETURN_MUTATION = `mutation MonsgaReturnRequest($orderId: ID!, $requestedLineItems: [RequestedLineItemInput!]!) {
  orderRequestReturn(orderId: $orderId, requestedLineItems: $requestedLineItems) {
    return { id name }
    userErrors { field message }
  }
}`;

const SUBSCRIBE_MUTATION = `mutation MonsgaSubscribe {
  customerEmailMarketingSubscribe {
    emailAddress { marketingState }
    userErrors { field message }
  }
}`;

const PROFILE_MUTATION = `mutation MonsgaProfileUpdate($input: CustomerUpdateInput!) {
  customerUpdate(input: $input) {
    customer { id displayName firstName lastName }
    userErrors { field message }
  }
}`;

const humanizeStatus = (value) => {
  if (!value) return '';
  const text = String(value).toLowerCase().replace(/_/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const mutationMessage = (result) => {
  if (!result || result.unauthorized) return accountStrings.error || '';

  if (Array.isArray(result.errors) && result.errors.length) return result.errors[0].message || '';

  const payload = result.data ? Object.values(result.data)[0] : null;
  const userErrors = (payload && payload.userErrors) || [];
  return userErrors.length ? userErrors.map((item) => item.message).join(' · ') : '';
};

const formatMoney = (money) => {
  if (!money || money.amount == null) return '';

  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: money.currencyCode,
    }).format(Number(money.amount));
  } catch (error) {
    return `${money.amount} ${money.currencyCode}`;
  }
};

const formatDate = (value) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  try {
    return new Intl.DateTimeFormat(document.documentElement.lang || 'en', { dateStyle: 'medium' }).format(date);
  } catch (error) {
    return date.toISOString().slice(0, 10);
  }
};

const templateFrom = (root, name) => {
  const scope = (root && root.closest('.mm-entry')) || root || document;
  return scope.querySelector(`[data-mm-template="${name}"]`);
};

const ADDRESS_TEXT_FIELDS = ['firstName', 'lastName', 'address1', 'address2', 'city', 'zoneCode', 'zip', 'territoryCode', 'phoneNumber'];
const ADDRESS_SHORT_FIELDS = ['firstName', 'lastName', 'address1', 'address2', 'city', 'zip', 'phoneNumber'];
const ADDRESS_CODE_FIELDS = ['territoryCode', 'zoneCode'];
const COUNTRY_CODES = 'AD AE AF AG AI AL AM AO AR AT AU AW AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BW BY BZ CA CD CF CG CH CI CK CL CM CN CO CR CU CV CW CY CZ DE DJ DK DM DO DZ EC EE EG ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GT GW GY HK HN HR HT HU ID IE IL IM IN IQ IS IT JE JM JO JP KE KG KH KI KM KN KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MK ML MM MN MO MQ MR MS MT MU MV MW MX MY MZ NA NC NE NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'.split(' ');

const fieldNode = (form, field) => {
  const nodes = form.querySelectorAll(`[data-mm-address-field="${field}"]`);
  for (const node of nodes) {
    if (!node.hidden) return node;
  }
  return nodes[0] || null;
};

const provinceOptions = (countrySelect) => {
  const option = countrySelect && countrySelect.selectedOptions && countrySelect.selectedOptions[0];
  const raw = option && option.getAttribute('data-provinces');
  if (!raw) return [];
  try {
    return JSON.parse(raw) || [];
  } catch (error) {
    return [];
  }
};

// Country list is rendered by Shopify (all_country_option_tags); each country option
// carries its own province list, so State/Province becomes a pick-list instead of free
// text. Countries without a province list keep the plain text input.
const syncProvinceField = (form, wanted) => {
  const country = form.querySelector('[data-mm-address-field="territoryCode"]');
  const select = form.querySelector('[data-mm-province-select]');
  const text = form.querySelector('[data-mm-province-text]');
  if (!select || !text) return;

  const provinces = provinceOptions(country);
  const current = wanted === undefined ? '' : String(wanted || '');

  if (!provinces.length) {
    select.hidden = true;
    text.hidden = false;
    text.value = current;
    syncProvinceInput(form);
    return;
  }

  select.textContent = '';
  provinces.forEach((province) => {
    const option = document.createElement('option');
    option.value = province[0];
    option.textContent = province[1] || province[0];
    select.append(option);
  });

  const match = provinces.find(
    (province) =>
      String(province[0]).toUpperCase() === current.toUpperCase() ||
      String(province[1] || '').toLowerCase() === current.toLowerCase()
  );
  if (current && !match) {
    const option = document.createElement('option');
    option.value = current;
    option.textContent = current;
    select.append(option);
  }

  text.value = "";
  select.hidden = false;
  text.hidden = true;
  select.value = match ? match[0] : current;
  syncProvinceInput(form);
};

// batch 244: the State/Province pick-list is only ~57 entries, but it becomes a searchable
// combo exactly like the country field. The <select> stays the value holder; this mirrors its
// selected label into the visible search box, and hides the box for countries that have no
// province list (there the free-text input takes over).
const syncProvinceInput = (form) => {
  const combo = form.querySelector('[data-mm-province-combo]');
  const search = form.querySelector('[data-mm-province-search]');
  const select = form.querySelector('[data-mm-province-select]');
  const free = !select || select.hidden;
  const option = !free && select.options[select.selectedIndex] ? select.options[select.selectedIndex] : null;
  if (combo) combo.hidden = free;
  if (search) search.value = option ? option.textContent.trim() : '';
};
const fillAddressForm = (form, address) => {
  ADDRESS_TEXT_FIELDS.forEach((field) => {
    const input = fieldNode(form, field);
    if (input) input.value = address && address[field] ? address[field] : '';
  });

  const country = countrySelectOf(form);
  if (country) {
    const wanted = String((address && address.territoryCode) || '').toUpperCase();
    const option = wanted ? Array.from(country.options).find((node) => optionIso(node) === wanted) : null;
    if (option) country.value = option.value;
    else if (wanted) country.append(new Option(wanted, wanted));
    else if (country.dataset.mmDefault) country.value = country.dataset.mmDefault;
  }
  syncProvinceField(form, (address && address.zoneCode) || '');
  syncCountryInput(form);

  const checkbox = form.querySelector('[data-mm-address-field="defaultAddress"]');
  if (checkbox) checkbox.checked = Boolean(address && address.isDefault);

  form.dataset.mmAddressId = (address && address.id) || '';
};

const readAddressForm = (form) => {
  const value = (field) => {
    const input = fieldNode(form, field);
    return input && input.value ? input.value.trim() : '';
  };

  const address = {};
  ADDRESS_SHORT_FIELDS.forEach((field) => {
    const fieldValue = value(field);
    if (fieldValue) address[field] = fieldValue;
  });

  ADDRESS_CODE_FIELDS.forEach((field) => {
    const fieldValue = value(field);
    // Province lists carry full names (New York) as well as codes (NY): upper-case codes only.
    if (fieldValue) address[field] = /^[a-z]{2}$/i.test(fieldValue) ? fieldValue.toUpperCase() : fieldValue;
  });

  // The country select stores country names, the account API wants an ISO-2 territoryCode.
  const territoryCode = optionIso(countrySelectedOption(form));
  if (territoryCode) address.territoryCode = territoryCode;
  else delete address.territoryCode;

  const checkbox = form.querySelector('[data-mm-address-field="defaultAddress"]');
  return { address, defaultAddress: Boolean(checkbox && checkbox.checked) };
};

const populateCountrySelect = (select) => {
  if (!select) return;

  // Shopify renders the country list (all_country_option_tags); the hardcoded list
  // below is only the offline fallback so an empty select can never ship.
  if (!select.options.length) {
    const names =
      typeof Intl.DisplayNames === 'function'
        ? new Intl.DisplayNames([document.documentElement.lang || 'en'], { type: 'region' })
        : null;

    COUNTRY_CODES.forEach((code) => {
      const option = document.createElement('option');
      option.value = code;
      option.textContent = (names && names.of(code)) || code;
      select.append(option);
    });
  }

  const market = ((entryConfig && entryConfig.countryIso) || (window.Shopify && window.Shopify.country) || '').toUpperCase();
  const home = Array.from(select.options).find((option) => optionIso(option) === market) || Array.from(select.options).find((option) => optionIso(option));
  if (home) select.dataset.mmDefault = home.value;
};

const COUNTRY_ALIASES = {
  america: 'US',
  american: 'US',
  usa: 'US',
  states: 'US',
  'united states': 'US',
  'united states of america': 'US',
  uk: 'GB',
  britain: 'GB',
  'great britain': 'GB',
  england: 'GB',
  scotland: 'GB',
  wales: 'GB',
  'northern ireland': 'GB',
  'united kingdom': 'GB',
  holland: 'NL',
  netherlands: 'NL',
  deutschland: 'DE',
  germany: 'DE',
  espana: 'ES',
  spain: 'ES',
  italia: 'IT',
  italy: 'IT',
  france: 'FR',
  uae: 'AE',
  emirates: 'AE',
  'united arab emirates': 'AE',
  korea: 'KR',
  'south korea': 'KR',
  czechia: 'CZ',
  'czech republic': 'CZ',
  brasil: 'BR',
  brazil: 'BR',
  hongkong: 'HK',
  'hong kong': 'HK',
  zealand: 'NZ',
  'new zealand': 'NZ',
};

const normalizeCountryQuery = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z\s.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// Shopify's all_country_option_tags renders the country NAME as the option value
// (United States), while the account API wants an ISO-2 territoryCode: bridged here.
const ISO_EXTRA = ['AC', 'IC', 'TA', 'XK']; // Shopify ships these regions; they are outside COUNTRY_CODES.

let countryNameIndex = null;
const countryCodeByLabel = () => {
  if (countryNameIndex) return countryNameIndex;
  const map = new Map();
  const codes = COUNTRY_CODES.concat(ISO_EXTRA);
  [document.documentElement.lang || 'en', 'en'].forEach((language) => {
    let names = null;
    try {
      names = new Intl.DisplayNames([language], { type: 'region' });
    } catch (error) {
      names = null;
    }
    if (!names) return;
    codes.forEach((code) => {
      let label = '';
      try {
        label = normalizeCountryQuery(names.of(code));
      } catch (error) {
        label = '';
      }
      if (label && !map.has(label)) map.set(label, code);
    });
  });
  countryNameIndex = map;
  return map;
};

// Options that already carry an ISO code (the offline fallback list) are taken as they are.
const optionIso = (option) => {
  const value = String((option && option.value) || '').trim();
  if (/^[A-Za-z]{2}$/.test(value)) return value.toUpperCase();
  const label = String((option && option.textContent) || '').trim();
  return countryCodeByLabel().get(normalizeCountryQuery(label)) || '';
};

const countrySelectOf = (form) => form.querySelector('select[data-mm-address-field="territoryCode"]');

const countrySelectedOption = (form) => {
  const select = countrySelectOf(form);
  return select && select.options[select.selectedIndex] ? select.options[select.selectedIndex] : null;
};

// Keeps the search field in step with the select, so an existing address opens on its own country.
const syncCountryInput = (form) => {
  const search = form.querySelector('[data-mm-country-search]');
  const option = countrySelectedOption(form);
  if (search) search.value = option && optionIso(option) ? option.textContent.trim() : '';
};

// Ranked candidates, never a silent guess: us offers United States ahead of Australia, and the
// short forms in COUNTRY_ALIASES (USA, UK, Deutschland) land on exactly one country.
const findCountryMatches = (select, rawQuery, limit) => {
  const query = normalizeCountryQuery(rawQuery);
  if (!query) return [];
  const alias = COUNTRY_ALIASES[query] || COUNTRY_ALIASES[query.split('.').join('')];
  const iso = /^[a-z]{2}$/.test(query) ? query.toUpperCase() : '';
  return Array.from(select.options)
    .map((option, index) => {
      const code = optionIso(option);
      const label = normalizeCountryQuery(option.textContent);
      let rank = -1;
      if (alias) rank = code === alias ? 0 : -1;
      else if (iso && code === iso) rank = 0;
      else if (label === query) rank = 1;
      else if (label.indexOf(query) === 0) rank = 2;
      else if (label.split(' ').some((word) => word.indexOf(query) === 0)) rank = 3;
      else if (label.indexOf(query) > 0) rank = 4;
      return { option, index, rank };
    })
    .filter((hit) => hit.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .slice(0, limit || 40)
    .map((hit) => hit.option);
};

// batch 244: one combo core serves both the country field (~250 entries) and the
// State/Province field (~57 entries). Typing fills a candidate list, Up/Down move a highlight
// inside it, Enter takes the highlight, and picking writes the hidden <select> that saves.
let comboSeq = 0;
const initCombo = (spec) => {
  const { combo, search, select, list, candidates, browsing, syncInput } = spec;
  const match = spec.match || findCountryMatches;
  if (!combo || !search || !select || !list) return;
  if (!list.id) list.id = `mm-combo-list-${(comboSeq += 1)}`;

  let hits = [];
  let active = -1;

  const items = () => Array.from(list.querySelectorAll('.mm-dashboard__combo-option'));

  const close = () => {
    hits = [];
    active = -1;
    list.hidden = true;
    list.textContent = '';
    search.setAttribute('aria-expanded', 'false');
    search.removeAttribute('aria-activedescendant');
  };

  const choose = (option) => {
    select.value = option.value;
    select.dispatchEvent(new Event('change'));
    close();
  };

  // batch 244: keyboard parity with the mouse - the highlighted row is what Enter takes.
  const paint = (index) => {
    const buttons = items();
    if (!buttons.length) return;
    active = Math.max(0, Math.min(buttons.length - 1, index));
    buttons.forEach((button, position) => {
      button.classList.toggle('is-active', position === active);
      if (position !== active) return;
      if (!button.id) button.id = `${list.id}-option-${position}`;
      search.setAttribute('aria-activedescendant', button.id);
      if (button.scrollIntoView) button.scrollIntoView({ block: 'nearest' });
    });
  };

  const open = () => {
    hits = browsing() ? candidates() : match(select, search.value, 60);
    list.textContent = '';
    active = -1;
    search.removeAttribute('aria-activedescendant');
    if (!hits.length) {
      close();
      return;
    }
    hits.forEach((option) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'mm-dashboard__combo-option';
      button.setAttribute('role', 'option');
      button.textContent = option.textContent.trim();
      button.addEventListener('click', () => choose(option));
      item.append(button);
      list.append(item);
    });
    list.hidden = false;
    search.setAttribute('aria-expanded', 'true');
  };

  search.addEventListener('input', open);
  search.addEventListener('focus', open);
  search.addEventListener('click', open);
  search.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (list.hidden) open();
      const buttons = items();
      if (!buttons.length) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      paint(active < 0 ? (step > 0 ? 0 : buttons.length - 1) : active + step);
      return;
    }
    // While browsing, the list is the whole list, so Enter must not silently take the first
    // row - it only picks once the query narrowed things or an arrow key moved the highlight.
    if (event.key === 'Enter' && hits.length) {
      const position = active >= 0 ? active : (!browsing() ? 0 : -1);
      if (position >= 0) {
        event.preventDefault();
        choose(hits[position]);
      }
    }
  });
  search.addEventListener('blur', () => {
    // Typing alone never changes the real value, so leaving the field puts the text back in
    // step with the select instead of leaving a half typed entry on screen.
    window.setTimeout(() => {
      close();
      syncInput();
    }, 150);
  });

  // Any change (list pick, form fill, programmatic edit) keeps the visible text honest.
  select.addEventListener('change', () => {
    syncInput();
    close();
  });
};

// The country list is ~250 entries long, so the field doubles as a search box: what you type
// fills a candidate list, and picking a candidate fills the hidden select that actually saves.
const initCountrySearch = (form) => {
  const search = form.querySelector('[data-mm-country-search]');

  // batch 243: an arrow means "click me for the whole list". Browsing is "the box is not a
  // query yet": empty, or still showing the country the select already holds (that is what
  // the form fills in on open). Real typing turns the same list into a filtered one.
  const browsing = () => {
    const label = ((countrySelectedOption(form) || {}).textContent || '').trim();
    if (!search) return true;
    return !normalizeCountryQuery(search.value) || (Boolean(label) && search.value.trim() === label);
  };

  // Shopify ships a bare "---" placeholder in its country option list; it is not a country.
  const candidates = () => {
    const select = countrySelectOf(form);
    if (!select) return [];
    return Array.from(select.options).filter(
      (option) => option.value && option.textContent.trim() && !/^[-\s]+$/.test(option.textContent.trim())
    );
  };

  initCombo({
    combo: form.querySelector('[data-mm-country-combo]'),
    search,
    select: countrySelectOf(form),
    list: form.querySelector('[data-mm-country-list]'),
    candidates,
    browsing,
    syncInput: () => syncCountryInput(form)
  });
};

// batch 245: Shopify's own province lists carry names only (["Alabama","Alabama"]), so a
// visitor typing a postal abbreviation ("NY", "BC", "DC") got no candidate at all. The US and
// Canadian code sets are disjoint, so one table serves both, and it only fires when the whole
// query is a known code - everything else keeps the plain name search.
const PROVINCE_CODES = {
  AK: 'Alaska',
  AL: 'Alabama',
  AR: 'Arkansas',
  AS: 'American Samoa',
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'Washington DC',
  DE: 'Delaware',
  FL: 'Florida',
  FM: 'Micronesia',
  GA: 'Georgia',
  GU: 'Guam',
  HI: 'Hawaii',
  IA: 'Iowa',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  MA: 'Massachusetts',
  MD: 'Maryland',
  ME: 'Maine',
  MH: 'Marshall Islands',
  MI: 'Michigan',
  MN: 'Minnesota',
  MO: 'Missouri',
  MP: 'Northern Mariana Islands',
  MS: 'Mississippi',
  MT: 'Montana',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  PW: 'Palau',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VA: 'Virginia',
  VI: 'U.S. Virgin Islands',
  VT: 'Vermont',
  WA: 'Washington',
  WI: 'Wisconsin',
  WV: 'West Virginia',
  WY: 'Wyoming',
  AA: 'Armed Forces Americas',
  AE: 'Armed Forces Europe',
  AP: 'Armed Forces Pacific',
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

const findProvinceMatches = (select, rawQuery, limit) => {
  const name = PROVINCE_CODES[normalizeCountryQuery(rawQuery).toUpperCase()];
  return findCountryMatches(select, name || rawQuery, limit);
};

const initProvinceSearch = (form) => {
  const search = form.querySelector('[data-mm-province-search]');
  const selectOf = () => form.querySelector('[data-mm-province-select]');

  // Same browsing rule as the country box: empty, or still showing the province the select
  // already holds. Countries without a province list hide the combo entirely.
  const browsing = () => {
    const select = selectOf();
    const option = select && select.options[select.selectedIndex] ? select.options[select.selectedIndex] : null;
    const label = option ? option.textContent.trim() : '';
    if (!search) return true;
    return !normalizeCountryQuery(search.value) || (Boolean(label) && search.value.trim() === label);
  };

  initCombo({
    combo: form.querySelector('[data-mm-province-combo]'),
    search,
    select: selectOf(),
    list: form.querySelector('[data-mm-province-list]'),
    candidates: () => {
      const select = selectOf();
      return select ? Array.from(select.options) : [];
    },
    browsing,
    match: findProvinceMatches,
    syncInput: () => syncProvinceInput(form)
  });
};const copyToClipboard = async (value) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const helper = document.createElement('textarea');
  helper.value = value;
  helper.setAttribute('readonly', 'readonly');
  helper.style.position = 'fixed';
  helper.style.opacity = '0';
  document.body.append(helper);
  helper.select();
  document.execCommand('copy');
  helper.remove();
};

const parseCouponCodes = (customer) => {
  const field = (((customer || {}).metafields) || []).find((entry) => entry && entry.key === 'monsga_coupons');
  const raw = String((field && field.value) || '').trim();
  if (!raw) return [];
  let values = raw.charAt(0) === '[' ? [] : raw.split(/\r?\n/);
  if (raw.charAt(0) === '[') {
    try {
      values = JSON.parse(raw);
    } catch (error) {
      values = [];
    }
  }
  const own = String((entryConfig && entryConfig.rewardsCodeFallback) || '').trim().toUpperCase();
  const seen = new Set();
  return values
    .map((value) => String(value).trim())
    .filter((value) => {
      const key = value.toUpperCase();
      if (!value || key === own || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const renderCouponCodes = (dashboard, customer) => {
  const view = dashboard.querySelector('[data-mm-dashboard-view="coupons"]');
  const template = templateFrom(dashboard, 'coupon');
  if (!view || !template) return;
  const grid = view.querySelector('.mm-dashboard__reward-grid');
  if (!grid) return;
  grid.querySelectorAll('[data-mm-coupon-card][data-mm-coupon-extra]').forEach((card) => card.remove());
  parseCouponCodes(customer).forEach((code) => {
    const card = template.content.firstElementChild.cloneNode(true);
    card.setAttribute('data-mm-coupon-card', '');
    card.setAttribute('data-mm-coupon-extra', '');
    card.querySelector('[data-mm-coupon-code]').textContent = code;
    grid.append(card);
  });
};


// The copy control is an icon, so the confirmation is a class (tick + green) plus an
// accessible-name swap rather than the old text swap.
const flashCopied = (button) => {
  if (!button) return;
  if (!button.dataset.mmCopyLabel) button.dataset.mmCopyLabel = button.getAttribute('aria-label') || '';
  const label = accountStrings.copied || button.dataset.mmCopyLabel;
  button.classList.add('is-copied');
  button.setAttribute('aria-label', label);
  button.setAttribute('title', label);
  window.clearTimeout(button.mmCopyTimer);
  button.mmCopyTimer = window.setTimeout(() => {
    button.classList.remove('is-copied');
    button.setAttribute('aria-label', button.dataset.mmCopyLabel);
    button.setAttribute('title', button.dataset.mmCopyLabel);
  }, 2000);
};

const USED_CODES_QUERY = `query MonsgaUsedCodes {
  customer {
    orders(first: 50, reverse: true) {
      nodes {
        discountApplications(first: 10) {
          nodes {
            ... on DiscountCodeApplication { code }
          }
        }
      }
    }
  }
}`;

const couponStateLabel = (state) => {
  const labels = {
    active: accountStrings.couponTabActive || 'Active',
    used: accountStrings.couponTabUsed || 'Used',
    expired: accountStrings.couponTabExpired || 'Expired',
  };
  return labels[state] || '';
};

// Coupons share one grid: filter by state, rewrite the state flag, and show an
// empty note when the chosen tab has nothing in it.
const refreshCouponView = (dashboard) => {
  const view = dashboard.querySelector('[data-mm-dashboard-view="coupons"]');
  const grid = view && view.querySelector('[data-mm-coupon-grid]');
  if (!view || !grid) return;
  const currentTab = view.querySelector('[data-mm-coupon-tab].is-active');
  const filter = (currentTab && currentTab.getAttribute('data-mm-coupon-tab')) || 'all';

  let visible = 0;
  view.querySelectorAll('[data-mm-coupon-card]').forEach((card) => {
    const state = card.getAttribute('data-mm-coupon-state') || 'active';
    const flag = card.querySelector('[data-mm-coupon-state-flag]');
    if (flag) {
      flag.textContent = couponStateLabel(state);
      flag.hidden = false;
    }
    card.hidden = !(filter === 'all' || filter === state);
    if (!card.hidden) visible += 1;
  });

  let empty = view.querySelector('[data-mm-coupon-empty]');
  if (!empty) {
    empty = document.createElement('p');
    empty.className = 'mm-dashboard__coupon-empty';
    empty.setAttribute('data-mm-coupon-empty', '');
    grid.after(empty);
  }
  empty.textContent = accountStrings.couponEmpty || '';
  empty.hidden = visible > 0;
};

const initCouponTabs = (dashboard) => {
  const view = dashboard.querySelector('[data-mm-dashboard-view="coupons"]');
  if (!view) return;
  view.querySelectorAll('[data-mm-coupon-tab]').forEach((tab) => {
    tab.addEventListener('click', () => {
      view.querySelectorAll('[data-mm-coupon-tab]').forEach((other) => {
        const isOn = other === tab;
        other.classList.toggle('is-active', isOn);
        other.setAttribute('aria-selected', isOn ? 'true' : 'false');
      });
      refreshCouponView(dashboard);
    });
  });
  refreshCouponView(dashboard);
};

// Shopify has no "which codes does this member hold" endpoint, so the used state is
// derived from the discount codes on the member's own orders.
const loadUsedCodes = async (dashboard) => {
  try {
    const result = await accountQuery(USED_CODES_QUERY);
    const customer = (result && result.data && result.data.customer) || null;
    if (!customer) return;
    const codes = (((customer.orders || {}).nodes) || []).flatMap((order) =>
      ((((order || {}).discountApplications || {}).nodes) || [])
        .map((application) => application && application.code)
        .filter(Boolean)
    );
    const used = new Set(codes.map((code) => String(code).toUpperCase()));
    if (!used.size) return;
    dashboard.querySelectorAll('[data-mm-coupon-card]').forEach((card) => {
      const codeNode = card.querySelector('[data-mm-coupon-code], [data-mm-reward-code]');
      const code = String((codeNode && codeNode.textContent) || '').trim().toUpperCase();
      if (code && used.has(code)) {
        card.setAttribute('data-mm-coupon-state', 'used');
        card.classList.add('is-used');
      }
    });
    refreshCouponView(dashboard);
  } catch (error) {
    // Used-state detection is a bonus: never let it break the coupons view.
  }
};

const loadCouponCodes = async (dashboard) => {
  try {
    const result = await accountQuery(COUPON_QUERY);
    const customer = (result && result.data && result.data.customer) || null;
    if (customer) renderCouponCodes(dashboard, customer);
    refreshCouponView(dashboard);
  } catch (error) {
    // Coupon extras are optional: never let them break the dashboard.
  }
};

const renderRewards = (dashboard, subscribed) => {
  const set = (selector, value, visible) => {
    const node = dashboard.querySelector(selector);
    if (!node) return;
    if (value != null) node.textContent = String(value);
    if (visible != null) node.hidden = !visible;
  };

  const code = subscribed ? ((entryConfig && entryConfig.rewardsCodeFallback) || '') : '';

  set('[data-mm-reward-title]', subscribed ? accountStrings.codeTitle || '' : accountStrings.lockedTitle || '');
  set('[data-mm-reward-state]', subscribed ? accountStrings.marketingOn || '' : accountStrings.marketingOff || '');
  set('[data-mm-reward-code]', code);
  set('[data-mm-reward-code-row]', null, Boolean(code));
  set('[data-mm-reward-locked]', accountStrings.lockedText || '', !subscribed);
  set('[data-mm-reward-subscribe]', null, !subscribed);
  set('[data-mm-rewards-summary]', subscribed ? accountStrings.marketingOn || '' : accountStrings.marketingOff || '');

  const shopLink = dashboard.querySelector('[data-mm-reward-shop]');
  if (shopLink && entryConfig && entryConfig.rewardsUrl) shopLink.href = entryConfig.rewardsUrl;
};

const renderLoyalty = (dashboard, customer) => {
  const card = dashboard.querySelector('[data-mm-loyalty]');
  if (!card) return;

  const context = {
    id: customer.id || '',
    email: (customer.emailAddress || {}).emailAddress || '',
    name: customer.displayName || '',
    app: (entryConfig && entryConfig.loyaltyApp) || '',
  };

  window.MONSGA_ACCOUNT = context;

  const provider = window.MONSGA_Loyalty;
  const render = typeof provider === 'function' ? provider : provider && provider.render;
  const mount = card.querySelector('[data-mm-loyalty-mount]');
  if (typeof render !== 'function' || !mount) return;

  mount.hidden = false;
  try {
    render(mount, context);
  } catch (error) {
    mount.hidden = true;
  }
};

const appendOrderRows = (dashboard, orderNodes, returnableOrders) => {
  const orderList = dashboard.querySelector('[data-mm-order-list]');
  const orderTemplate = templateFrom(dashboard, 'order');
  const trackingTemplate = templateFrom(dashboard, 'tracking');
  const returnTemplate = templateFrom(dashboard, 'return');
  if (!orderList || !orderTemplate) return;

  orderNodes.forEach((order) => {
    const row = orderTemplate.content.firstElementChild.cloneNode(true);
    const quantity = ((order.lineItems || {}).nodes || []).reduce((total, item) => total + (item.quantity || 0), 0);

    row.querySelector('[data-mm-order-name]').textContent = order.name || '';
    row.querySelector('[data-mm-order-date]').textContent = formatDate(order.processedAt);
    row.querySelector('[data-mm-order-count]').textContent = String(quantity);
    const orderUnit = row.querySelector('[data-mm-order-unit]');
    if (orderUnit) orderUnit.textContent = quantity === 1 ? (accountStrings.itemLabel || '') : (accountStrings.itemsLabel || '');
    row.querySelector('[data-mm-order-total]').textContent = formatMoney(order.totalPrice);

    const itemsList = row.querySelector('[data-mm-order-items-list]');
    const lineItems = ((order.lineItems || {}).nodes || []);
    if (itemsList && lineItems.length) {
      lineItems.forEach((item) => {
        const node = document.createElement('span');
        node.className = 'mm-dashboard__order-item';
        const imageUrl = item.image && item.image.url;
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = String((item.image.altText || item.name || '')).trim();
          image.loading = 'lazy';
          image.width = 40;
          image.height = 40;
          node.append(image);
        }
        const label = document.createElement('span');
        label.textContent = (item.name || '') + ' × ' + (item.quantity || 0);
        node.append(label);
        itemsList.append(node);
      });
      const itemsBlock = row.querySelector('[data-mm-order-items]');
      if (itemsBlock) itemsBlock.hidden = false;
    }

    const orderMoney = (value) => (value && value.amount != null ? formatMoney(value) : '');
    const orderFigure = (label, value) => {
      const node = document.createElement('span');
      node.className = 'mm-dashboard__order-figure';
      const key = document.createElement('span');
      key.className = 'mm-dashboard__order-figure-label';
      key.textContent = label;
      const amount = document.createElement('span');
      amount.className = 'mm-dashboard__order-figure-value';
      amount.textContent = value;
      node.append(key, amount);
      return node;
    };
    const totalsList = row.querySelector('[data-mm-order-totals-list]');
    if (totalsList) {
      const entries = [
        [accountStrings.orderSubtotal || 'Subtotal', order.subtotal],
        [accountStrings.orderShipping || 'Shipping', order.totalShipping],
        [accountStrings.orderTax || 'Tax', order.totalTax],
        [accountStrings.orderTotal || 'Total', order.totalPrice],
      ].filter((entry) => orderMoney(entry[1]) !== '');
      entries.forEach(([label, value]) => {
        totalsList.append(orderFigure(label, orderMoney(value)));
      });
      const totalsBlock = row.querySelector('[data-mm-order-totals]');
      if (totalsBlock) totalsBlock.hidden = entries.length === 0;
    }

    const paymentLabel = orderPaymentLabel(order.financialStatus);
    const paymentList = row.querySelector('[data-mm-order-payment-list]');
    if (paymentList) {
      const refunded = orderMoney(order.totalRefunded);
      if (paymentLabel) {
        const refundedBack = refunded && refunded.indexOf('0.00') === -1;
        // The section already says "Payment"; the row only carries the value.
        paymentList.append(orderFigure(
          '',
          refundedBack ? paymentLabel + ' · ' + refunded : paymentLabel,
        ));
      }
      const paymentBlock = row.querySelector('[data-mm-order-payment]');
      if (paymentBlock) paymentBlock.hidden = !paymentLabel;
    }

    const addressBlock = row.querySelector('[data-mm-order-address]');
    if (addressBlock) {
      const fillAddress = (selector, address) => {
        const node = row.querySelector(selector);
        if (!node) return;
        const lines = (address && Array.isArray(address.formatted) && address.formatted) || [];
        node.textContent = lines.length ? lines.join(', ') : (accountStrings.notSet || '');
      };
      fillAddress('[data-mm-order-shipping-address]', order.shippingAddress);
      fillAddress('[data-mm-order-billing-address]', order.billingAddress);
      addressBlock.hidden = !order.shippingAddress && !order.billingAddress;
    }

    const viewLink = row.querySelector('[data-mm-order-view]');
    if (viewLink) {
      if (order.statusPageUrl) viewLink.href = order.statusPageUrl;
      else viewLink.hidden = true;
    }

    const paymentChip = row.querySelector('[data-mm-order-payment-chip]');
    if (paymentChip && paymentLabel) {
      paymentChip.textContent = paymentLabel;
      paymentChip.setAttribute('data-mm-chip-state', String(order.financialStatus || '').toLowerCase());
      paymentChip.hidden = false;
    }

    const orderStatus = row.querySelector('[data-mm-order-status]');
    if (orderStatus) {
      const shipment = orderFulfillmentState(order);
      orderStatus.textContent = orderFulfillmentLabel(shipment);
      orderStatus.setAttribute('data-mm-chip-state', shipment);
      orderStatus.hidden = false;
    }

    const trackingList = row.querySelector('[data-mm-tracking-list]');
    const trackingNodes = ((order.fulfillments || {}).nodes || []).flatMap((item) => item.trackingInformation || []);
    if (trackingList && trackingTemplate && trackingNodes.length) {
      trackingNodes.forEach((info) => {
        const node = trackingTemplate.content.firstElementChild.cloneNode(true);
        node.querySelector('[data-mm-tracking-company]').textContent = info.company || '';
        node.querySelector('[data-mm-tracking-number]').textContent = info.number || '';
        const link = node.querySelector('[data-mm-tracking-link]');
        if (info.url) link.href = info.url;
        else link.remove();
        trackingList.append(node);
      });
      row.querySelector('[data-mm-order-tracking]').hidden = false;
    }

    const returnNodes = (order.returns || {}).nodes || [];
    const returnsList = row.querySelector('[data-mm-order-returns-list]');
    if (returnsList && returnTemplate && returnNodes.length) {
      returnNodes.forEach((item) => {
        const node = returnTemplate.content.firstElementChild.cloneNode(true);
        node.querySelector('[data-mm-return-name]').textContent = item.name || '';
        node.querySelector('[data-mm-return-date]').textContent = formatDate(item.createdAt);
        const statusBadge = node.querySelector('[data-mm-return-status]');
        const statusLabel = returnStatusLabels[item.status];
        if (statusBadge && statusLabel) {
          statusBadge.textContent = statusLabel;
          statusBadge.hidden = false;
        }
        returnsList.append(node);
      });
      row.querySelector('[data-mm-order-returns]').hidden = false;
    }

    const returnButton = row.querySelector('[data-mm-order-return]');
    const returnable = ((order.returnInformation || {}).returnableLineItems || {}).nodes || [];
    if (returnButton && returnable.length) {
      returnableOrders.set(order.id, { name: order.name || '', lines: returnable });
      returnButton.dataset.mmReturnOrder = order.id;
      returnButton.hidden = false;
    }

    const toggle = row.querySelector('[data-mm-order-toggle]');
    const body = row.querySelector('[data-mm-order-body]');
    if (toggle && body) {
      toggle.addEventListener('click', () => {
        const open = body.hidden;
        body.hidden = !open;
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        row.classList.toggle('is-open', open);
      });
    }

    orderList.append(row);
  });
};

const renderAccount = (dashboard, customer) => {
  const email = (customer.emailAddress || {}).emailAddress || '';
  const name = customer.displayName || '';
  const phone = (customer.phoneNumber || {}).phoneNumber || '';
  const defaultAddressId = (customer.defaultAddress || {}).id || '';
  const addresses = ((customer.addresses || {}).nodes || [])
    .map((address) => ({ ...address, isDefault: address.id === defaultAddressId }))
    .sort((left, right) => Number(right.isDefault) - Number(left.isDefault));
  const orders = ((customer.orders || {}).nodes || [])
    .slice()
    .sort((left, right) => String(right.processedAt).localeCompare(String(left.processedAt)));

  const write = (selector, value) => {
    const node = dashboard.querySelector(selector);
    if (node) node.textContent = value == null ? '' : String(value);
  };

  const shownName = name || email;
  const nameIsEmail = String(shownName || '').indexOf('@') > 0 && String(shownName || '').indexOf(' ') < 0;

  write('[data-mm-identity-name]', shownName);
  write('[data-mm-identity-initial]', nameIsEmail ? '@' : String(shownName || '?').trim().charAt(0).toUpperCase());

  const identityEmail = dashboard.querySelector('[data-mm-identity-email]');
  if (identityEmail) {
    const duplicate = !name || nameIsEmail || String(name).trim().toLowerCase() === String(email || '').trim().toLowerCase();
    identityEmail.textContent = duplicate ? '' : (email || '');
    identityEmail.hidden = duplicate;
  }
  write('[data-mm-count-orders]', orders.length);
  write('[data-mm-count-addresses]', addresses.length);

  const writeCountUnit = (selector, count) => {
    const unit = dashboard.querySelector(selector);
    if (!unit) return;
    const fallback = unit.textContent;
    unit.textContent = count === 1 ? (unit.getAttribute('data-mm-one') || fallback) : (unit.getAttribute('data-mm-many') || fallback);
  };
  writeCountUnit('[data-mm-orders-unit]', orders.length);
  writeCountUnit('[data-mm-addresses-unit]', addresses.length);
  write('[data-mm-profile-header]', email);
  write('[data-mm-profile-email]', email);
  write('[data-mm-profile-name]', name || accountStrings.notSet || '');
  write('[data-mm-profile-phone]', phone || accountStrings.notSet || '');

  const profileForm = dashboard.querySelector('[data-mm-profile-form]');
  if (profileForm) {
    profileForm.querySelectorAll('[data-mm-profile-field]').forEach((input) => {
      const loaded = customer[input.dataset.mmProfileField] || '';
      input.value = loaded;
      input.dataset.mmProfileLoaded = loaded;
    });
  }

  const orderList = dashboard.querySelector('[data-mm-order-list]');
  const orderTemplate = templateFrom(dashboard, 'order');
  const returnableOrders = new Map();
  if (orderList && orderTemplate) {
    orderList.textContent = '';

    appendOrderRows(dashboard, orders, returnableOrders);
    orderList.hidden = orders.length === 0;
  }

  const ordersPage = (customer.orders || {}).pageInfo || {};
  const ordersMore = dashboard.querySelector('[data-mm-orders-more]');
  if (ordersMore) ordersMore.hidden = !ordersPage.hasNextPage;

  const ordersEmpty = dashboard.querySelector('[data-mm-orders-empty]');
  if (ordersEmpty) ordersEmpty.hidden = orders.length > 0;

  const addressList = dashboard.querySelector('[data-mm-address-list]');
  const addressTemplate = templateFrom(dashboard, 'address');
  if (addressList && addressTemplate) {
    addressList.textContent = '';

    addresses.forEach((address) => {
      const card = addressTemplate.content.firstElementChild.cloneNode(true);
      card.dataset.mmAddressId = address.id;
      card.classList.toggle('is-default', address.isDefault);

      const badge = card.querySelector('[data-mm-address-badge]');
      if (badge && !address.isDefault) badge.remove();

      const fullName = [address.firstName, address.lastName].filter(Boolean).join(' ');
      card.querySelector('[data-mm-address-name]').textContent = fullName || address.city || '';

      const lines = Array.isArray(address.formatted) && address.formatted.length
        ? address.formatted
        : [address.address1, address.address2, [address.city, address.zip].filter(Boolean).join(' ')].filter(Boolean);
      card.querySelector('[data-mm-address-lines]').innerHTML = lines.map(escapeHtml).join('<br>');

      addressList.append(card);
    });

    addressList.hidden = addresses.length === 0;
  }

  const addressesEmpty = dashboard.querySelector('[data-mm-addresses-empty]');
  if (addressesEmpty) addressesEmpty.hidden = addresses.length > 0;

  renderRewards(dashboard, ((customer.emailAddress || {}).marketingState) === 'SUBSCRIBED');
  loadCouponCodes(dashboard);
  loadUsedCodes(dashboard);
  renderLoyalty(dashboard, customer);

  return { addresses, returnableOrders, ordersPage, orders };
};

const initMemberAccount = async () => {
  if (!entryConfig || !entryConfig.clientId) {
    // No client id: the hosted sign-in trigger is the only way in, so never leave
    // the panel hidden waiting for a query that cannot run.
    document.querySelectorAll('.mm-entry__panel[hidden]').forEach((node) => {
      node.hidden = false;
      const state = node.querySelector('[data-mm-signed-out]');
      if (state) state.hidden = false;
    });
    return;
  }

  const dashboard = document.querySelector('[data-mm-dashboard]');
  if (!dashboard) return;

  const section = dashboard.closest('.mm-entry');
  const panel = section ? section.querySelector('.mm-entry__panel') : null;
  const signedOut = section ? section.querySelector('[data-mm-signed-out]') : null;
  const status = dashboard.querySelector('[data-mm-status]');
  const form = dashboard.querySelector('[data-mm-address-form]');
  const formTitle = dashboard.querySelector('[data-mm-address-form-title]');
  const saveButton = dashboard.querySelector('[data-mm-address-save]');
  let addresses = [];
  let returnableOrders = new Map();
  let ordersCursor = null;
  let loadedOrders = [];
  let hasMoreOrders = false;
  let searchGeneration = 0;
  let searchTimer = 0;

  const setStatus = (message) => {
    if (!status) return;
    status.textContent = message || '';
    status.hidden = !message;
  };

  const showSignedOut = () => {
    if (panel) panel.hidden = false;
    if (signedOut) signedOut.hidden = false;
    dashboard.hidden = true;
    if (section) section.classList.remove('mm-entry--authenticated');
  };

  const showDashboard = () => {
    if (panel) panel.hidden = false;
    if (signedOut) signedOut.hidden = true;
    dashboard.hidden = false;
    if (section) section.classList.add('mm-entry--authenticated');
  };

  const load = async (message) => {
    setStatus(accountStrings.loading || '');

    const result = await accountQuery(ACCOUNT_QUERY);

    if (result.unauthorized) {
      showSignedOut();
      setStatus('');
      return false;
    }

    if (Array.isArray(result.errors) && result.errors.length) {
      showDashboard();
      setStatus(result.errors[0].message || accountStrings.error || '');
      return false;
    }

    const customer = result.data && result.data.customer;
    if (!customer) {
      showDashboard();
      setStatus(accountStrings.error || '');
      return false;
    }

    const rendered = renderAccount(dashboard, customer);
    addresses = rendered.addresses;
    returnableOrders = rendered.returnableOrders;
    loadedOrders = rendered.orders || [];
    hasMoreOrders = Boolean((rendered.ordersPage || {}).hasNextPage);
    ordersCursor = (rendered.ordersPage || {}).endCursor || null;
    showDashboard();

    // A refresh must re-run an active search instead of quietly showing the
    // unfiltered first page again.
    if (searchIsActive()) await applyOrderFilters();
    setStatus(message || '');
    return true;
  };

  const ordersMoreButton = dashboard.querySelector('[data-mm-orders-more]');
  if (ordersMoreButton) {
    ordersMoreButton.addEventListener('click', async () => {
      if (!ordersCursor || ordersMoreButton.disabled) return;

      ordersMoreButton.disabled = true;
      setStatus(accountStrings.loading || '');

      const result = await accountQuery(ORDERS_PAGE_QUERY, { after: ordersCursor });
      const page = result.data && result.data.customer && result.data.customer.orders;

      if (result.unauthorized) {
        showSignedOut();
        setStatus('');
      } else if (Array.isArray(result.errors) && result.errors.length) {
        setStatus(result.errors[0].message || accountStrings.error || '');
      } else if (!page) {
        setStatus(accountStrings.error || '');
      } else {
        appendOrderRows(dashboard, page.nodes || [], returnableOrders);
        loadedOrders = loadedOrders.concat(page.nodes || []);
        ordersCursor = (page.pageInfo || {}).endCursor || null;
        hasMoreOrders = Boolean((page.pageInfo || {}).hasNextPage);
        ordersMoreButton.hidden = !hasMoreOrders;
        setStatus('');
      }

      ordersMoreButton.disabled = false;
    });
  }

  // ---- order search + payment/fulfillment filters -----------------------------
  // Shopify has no server-side order search on the customer API, and the page
  // only ever holds the first page, so a search has to walk the customer's whole
  // order history itself before it can honestly answer "not found".
  const orderSearchInput = dashboard.querySelector('[data-mm-order-search]');
  const orderPaymentFilter = dashboard.querySelector('[data-mm-order-payment-filter]');
  const orderFulfillmentFilter = dashboard.querySelector('[data-mm-order-fulfillment-filter]');
  const orderResetButton = dashboard.querySelector('[data-mm-order-reset]');
  const orderProgress = dashboard.querySelector('[data-mm-order-progress]');
  const orderRetryButton = dashboard.querySelector('[data-mm-order-retry]');
  const orderSearchEmpty = dashboard.querySelector('[data-mm-order-search-empty]');
  const orderListEl = dashboard.querySelector('[data-mm-order-list]');
  const orderListEmpty = dashboard.querySelector('[data-mm-orders-empty]');

  const readOrderFilters = () => ({
    term: normalizeOrderTerm(orderSearchInput ? orderSearchInput.value : ''),
    payment: orderPaymentFilter ? orderPaymentFilter.value : '',
    fulfillment: orderFulfillmentFilter ? orderFulfillmentFilter.value : '',
  });

  const orderFiltersActive = (filters) => {
    const current = filters || readOrderFilters();
    return Boolean(current.term || current.payment || current.fulfillment);
  };

  const searchIsActive = () => orderFiltersActive(null);

  const setOrderProgress = (message, isError) => {
    if (!orderProgress) return;
    orderProgress.textContent = message || '';
    orderProgress.hidden = !message;
    orderProgress.classList.toggle('is-error', Boolean(isError));
    if (orderRetryButton) orderRetryButton.hidden = !isError;
  };

  const setOrderSearchEmpty = (visible) => {
    if (orderSearchEmpty) orderSearchEmpty.hidden = !visible;
  };

  // Resetting puts back the orders the page already holds, so clearing a filter
  // never refetches and never loses the pages the customer already opened.
  const restoreOrderList = () => {
    if (!orderListEl) return;
    orderListEl.textContent = '';
    returnableOrders.clear();
    appendOrderRows(dashboard, loadedOrders, returnableOrders);
    orderListEl.hidden = loadedOrders.length === 0;
    if (ordersMoreButton) ordersMoreButton.hidden = !hasMoreOrders;
  };

  // ponytail: hard ceiling, 12 pages x 20 = 240 orders per search. Raise it if any
  // member ever has more; the loop is the only thing that changes.
  const ORDER_SEARCH_PAGE_LIMIT = 12;

  const applyOrderFilters = async () => {
    if (!orderListEl) return;

    const filters = readOrderFilters();
    searchGeneration += 1;
    const generation = searchGeneration;

    if (!orderFiltersActive(filters)) {
      setOrderProgress('');
      setOrderSearchEmpty(false);
      if (orderListEmpty) orderListEmpty.hidden = loadedOrders.length > 0;
      restoreOrderList();
      return;
    }

    orderListEl.textContent = '';
    returnableOrders.clear();
    orderListEl.hidden = true;
    setOrderSearchEmpty(false);
    // "You have no orders" and "nothing matches" are different claims.
    if (orderListEmpty) orderListEmpty.hidden = true;
    if (ordersMoreButton) ordersMoreButton.hidden = true;
    setOrderProgress(accountStrings.ordersSearching || '');

    let cursor = null;
    let page = 0;
    let failed = false;
    let truncated = false;
    const matches = [];

    for (;;) {
      const result = await accountQuery(ORDERS_PAGE_QUERY, { after: cursor });

      // A newer keystroke already owns the view: drop this late answer.
      if (generation !== searchGeneration) return;

      if (result.unauthorized) {
        showSignedOut();
        setOrderProgress('');
        return;
      }

      if (Array.isArray(result.errors) && result.errors.length) {
        failed = true;
        break;
      }

      const ordersPage = (result.data && result.data.customer && result.data.customer.orders) || null;
      if (!ordersPage) {
        failed = true;
        break;
      }

      (ordersPage.nodes || []).forEach((order) => {
        if (orderMatchesFilters(order, filters)) matches.push(order);
      });

      page += 1;
      const info = ordersPage.pageInfo || {};
      if (!info.hasNextPage) break;
      if (page >= ORDER_SEARCH_PAGE_LIMIT) {
        truncated = true;
        break;
      }
      cursor = info.endCursor || null;
      if (!cursor) break;
    }

    if (generation !== searchGeneration) return;

    orderListEl.textContent = '';
    returnableOrders.clear();
    appendOrderRows(dashboard, matches, returnableOrders);
    orderListEl.hidden = matches.length === 0;

    if (failed || truncated) {
      // Never answer "no matching orders" from a walk that did not finish.
      setOrderSearchEmpty(false);
      setOrderProgress(accountStrings.ordersSearchFailed || '', true);
      return;
    }

    setOrderSearchEmpty(matches.length === 0);
    setOrderProgress(matches.length
      ? (String(matches.length) + ' ' + (accountStrings.ordersResultsLabel || '')).trim()
      : '');
  };

  const scheduleOrderSearch = () => {
    window.clearTimeout(searchTimer);
    searchTimer = window.setTimeout(applyOrderFilters, 300);
  };

  if (orderSearchInput) {
    let composing = false;
    orderSearchInput.addEventListener('compositionstart', () => { composing = true; });
    orderSearchInput.addEventListener('compositionend', () => {
      composing = false;
      scheduleOrderSearch();
    });
    orderSearchInput.addEventListener('input', () => {
      if (!composing) scheduleOrderSearch();
    });
    orderSearchInput.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        window.clearTimeout(searchTimer);
        applyOrderFilters();
        return;
      }
      if (event.key === 'Escape' && orderSearchInput.value) {
        event.preventDefault();
        orderSearchInput.value = '';
        applyOrderFilters();
      }
    });
  }

  [orderPaymentFilter, orderFulfillmentFilter].forEach((node) => {
    if (!node) return;
    node.addEventListener('change', () => {
      window.clearTimeout(searchTimer);
      applyOrderFilters();
    });
  });

  if (orderResetButton) {
    orderResetButton.addEventListener('click', () => {
      window.clearTimeout(searchTimer);
      if (orderSearchInput) orderSearchInput.value = '';
      if (orderPaymentFilter) orderPaymentFilter.value = '';
      if (orderFulfillmentFilter) orderFulfillmentFilter.value = '';
      applyOrderFilters();
    });
  }

  if (orderRetryButton) {
    orderRetryButton.addEventListener('click', () => {
      window.clearTimeout(searchTimer);
      applyOrderFilters();
    });
  }

  const countrySelect = form ? form.querySelector('[data-mm-address-field="territoryCode"]') : null;
  populateCountrySelect(countrySelect);
  if (countrySelect) countrySelect.addEventListener('change', () => syncProvinceField(form, ''));
  initCountrySearch(form);
  initProvinceSearch(form);
  initCouponTabs(dashboard);

  const signOutButton = dashboard.querySelector('[data-mm-signout]');
  if (signOutButton) {
    signOutButton.addEventListener('click', () => {
      const session = readAccount();
      clearAccount();

      if (session && session.idToken && entryConfig.logoutEndpoint) {
        const url = new URL(entryConfig.logoutEndpoint);
        url.searchParams.set('id_token_hint', session.idToken);
        url.searchParams.set('post_logout_redirect_uri', `${window.location.origin}${window.location.pathname}`);
        window.location.assign(url.toString());
        return;
      }

      window.location.reload();
    });
  }

  const openForm = (address) => {
    if (!form) return;

    fillAddressForm(form, address);
    if (formTitle) formTitle.textContent = address ? accountStrings.edit || '' : accountStrings.addAddress || '';
    if (saveButton) saveButton.textContent = address ? accountStrings.saveChanges || '' : accountStrings.saveAddress || '';
    form.hidden = false;
    if (form.scrollIntoView) form.scrollIntoView({ block: 'nearest' });
  };

  dashboard.querySelectorAll('[data-mm-address-new]').forEach((button) => {
    button.addEventListener('click', () => openForm(null));
  });

  dashboard.querySelectorAll('[data-mm-address-cancel]').forEach((button) => {
    button.addEventListener('click', () => {
      if (form) form.hidden = true;
    });
  });

  if (saveButton && form) {
    saveButton.addEventListener('click', async () => {
      const { address, defaultAddress } = readAddressForm(form);
      const addressId = form.dataset.mmAddressId || '';

      saveButton.disabled = true;
      setStatus(accountStrings.loading || '');

      const result = await accountQuery(
        addressId ? ADDRESS_MUTATIONS.update : ADDRESS_MUTATIONS.create,
        addressId ? { address, addressId, defaultAddress } : { address, defaultAddress }
      );

      saveButton.disabled = false;

      const message = mutationMessage(result);
      if (message) {
        setStatus(message);
        return;
      }

      form.hidden = true;
      await load(accountStrings.saved || '');
    });
  }

  const addressList = dashboard.querySelector('[data-mm-address-list]');
  if (addressList) {
    addressList.addEventListener('click', async (event) => {
      const card = event.target.closest('[data-mm-address-id]');
      if (!card) return;

      const address = addresses.find((item) => item.id === card.dataset.mmAddressId);
      if (!address) return;

      if (event.target.closest('[data-mm-address-edit]')) {
        openForm(address);
        return;
      }

      if (!event.target.closest('[data-mm-address-delete]')) return;
      if (!window.confirm(accountStrings.confirmDelete || '')) return;

      setStatus(accountStrings.loading || '');
      const result = await accountQuery(ADDRESS_MUTATIONS.remove, { addressId: address.id });
      const message = mutationMessage(result);
      if (message) {
        setStatus(message);
        return;
      }

      await load(accountStrings.deleted || '');
    });
  }

  const profileSaveButton = dashboard.querySelector('[data-mm-profile-save]');
  if (profileSaveButton) {
    profileSaveButton.addEventListener('click', async () => {
      profileSaveButton.disabled = true;
      setStatus(accountStrings.loading || '');

      const input = {};
      ['firstName', 'lastName'].forEach((key) => {
        const field = dashboard.querySelector(`[data-mm-profile-field="${key}"]`);
        const value = field ? field.value.trim() : '';
        if (value || (field && field.dataset.mmProfileLoaded)) input[key] = value;
      });

      const result = await accountQuery(PROFILE_MUTATION, { input });

      profileSaveButton.disabled = false;

      const message = mutationMessage(result);
      if (message) {
        setStatus(message);
        return;
      }

      setProfileEditing(false);
      await load(accountStrings.profileSaved || '');
    });
  }

  const profileForm = dashboard.querySelector('[data-mm-profile-form]');
  const profileView = dashboard.querySelector('[data-mm-profile-view]');
  const profileEditButton = dashboard.querySelector('[data-mm-profile-edit]');
  const profileCancelButton = dashboard.querySelector('[data-mm-profile-cancel]');

  function setProfileEditing(editing) {
    if (profileForm) profileForm.hidden = !editing;
    if (profileView) profileView.hidden = Boolean(editing);
    if (profileEditButton) profileEditButton.hidden = Boolean(editing);
  }

  if (profileEditButton) profileEditButton.addEventListener('click', () => setProfileEditing(true));

  if (profileCancelButton) {
    profileCancelButton.addEventListener('click', () => {
      // Cancel is a discard: put the loaded values back and never write.
      if (profileForm) {
        profileForm.querySelectorAll('[data-mm-profile-field]').forEach((input) => {
          input.value = input.dataset.mmProfileLoaded || '';
        });
      }
      setProfileEditing(false);
    });
  }

  setProfileEditing(false);

  const rewardsCopy = dashboard.querySelector('[data-mm-reward-copy]');
  if (rewardsCopy) {
    rewardsCopy.addEventListener('click', async () => {
      const node = dashboard.querySelector('[data-mm-reward-code]');
      const value = node ? node.textContent.trim() : '';
      if (!value) return;

      try {
        await copyToClipboard(value);
        flashCopied(rewardsCopy);
      } catch (error) {
        setStatus(accountStrings.error || '');
      }
    });
  }

  dashboard.addEventListener('click', async (event) => {
    const button = event.target && event.target.closest ? event.target.closest('[data-mm-coupon-copy]') : null;
    if (!button) return;
    const card = button.closest('[data-mm-coupon-card]');
    const codeNode = card ? card.querySelector('[data-mm-coupon-code]') : null;
    const value = codeNode ? codeNode.textContent.trim() : '';
    if (!value) return;
    try {
      await copyToClipboard(value);
      flashCopied(button);
    } catch (error) {
      setStatus(accountStrings.error || '');
    }
  });

  const subscribeButton = dashboard.querySelector('[data-mm-reward-subscribe]');
  if (subscribeButton) {
    subscribeButton.addEventListener('click', async () => {
      subscribeButton.disabled = true;
      setStatus(accountStrings.loading || '');

      const result = await accountQuery(SUBSCRIBE_MUTATION);
      subscribeButton.disabled = false;

      const message = mutationMessage(result);
      if (message) {
        setStatus(message);
        return;
      }

      await load(accountStrings.subscribed || '');
    });
  }

  const returnForm = dashboard.querySelector('[data-mm-return-form]');
  const returnItems = dashboard.querySelector('[data-mm-return-items]');
  const returnLineTemplate = templateFrom(dashboard, 'return-line');
  const returnSubmit = dashboard.querySelector('[data-mm-return-submit]');
  let activeReturnOrder = '';

  const closeReturnForm = () => {
    activeReturnOrder = '';
    if (returnForm) returnForm.hidden = true;
  };

  const openReturnForm = (orderId) => {
    if (!returnForm || !returnItems || !returnLineTemplate) return;

    const entry = returnableOrders.get(orderId);
    if (!entry) return;

    returnItems.textContent = '';
    activeReturnOrder = orderId;

    const orderNode = returnForm.querySelector('[data-mm-return-order]');
    if (orderNode) orderNode.textContent = entry.name;

    entry.lines.forEach((line) => {
      const item = line.lineItem || {};
      const row = returnLineTemplate.content.firstElementChild.cloneNode(true);
      row.dataset.mmLineItem = item.id || '';
      row.querySelector('[data-mm-return-line-name]').textContent = item.name || '';
      row.querySelector('[data-mm-return-line-qty-text]').textContent = String(line.quantity || 1);

      const qty = row.querySelector('[data-mm-return-line-qty]');
      if (qty) qty.max = String(line.quantity || 1);

      const select = row.querySelector('[data-mm-return-line-reason]');
      const reasonField = row.querySelector('[data-mm-return-line-reason-field]');
      const reasons = ((item.suggestedReturnReasonDefinitions || {}).nodes) || [];

      if (select && reasons.length) {
        const blank = document.createElement('option');
        blank.value = '';
        blank.textContent = accountStrings.notSet || '';
        select.append(blank);
        reasons.forEach((reason) => {
          const option = document.createElement('option');
          option.value = reason.id;
          option.textContent = reason.name;
          select.append(option);
        });
      } else if (reasonField) {
        reasonField.remove();
      }

      returnItems.append(row);
    });

    const emptyNote = dashboard.querySelector('[data-mm-return-empty]');
    if (emptyNote) emptyNote.hidden = entry.lines.length > 0;
    if (returnSubmit) returnSubmit.disabled = entry.lines.length === 0;

    returnForm.hidden = false;
    if (returnForm.scrollIntoView) returnForm.scrollIntoView({ block: 'nearest' });
  };

  const orderListNode = dashboard.querySelector('[data-mm-order-list]');
  if (orderListNode) {
    orderListNode.addEventListener('click', (event) => {
      const button = event.target.closest('[data-mm-order-return]');
      if (!button || button.hidden) return;
      openReturnForm(button.dataset.mmReturnOrder || '');
    });
  }

  if (returnSubmit && returnItems) {
    returnSubmit.addEventListener('click', async () => {
      if (!activeReturnOrder) return;

      const noteNode = dashboard.querySelector('[data-mm-return-note]');
      const note = ((noteNode && noteNode.value) || '').trim();
      const requested = [];

      returnItems.querySelectorAll('.mm-dashboard__return-line').forEach((row) => {
        const check = row.querySelector('[data-mm-return-line-check]');
        if (!check || !check.checked) return;

        const lineItemId = row.dataset.mmLineItem || '';
        if (!lineItemId) return;

        const qtyNode = row.querySelector('[data-mm-return-line-qty]');
        const line = {
          lineItemId,
          quantity: Math.max(1, Number(qtyNode && qtyNode.value) || 1),
        };
        if (note) line.customerNote = note.slice(0, 300);

        const select = row.querySelector('[data-mm-return-line-reason]');
        if (select && select.value) line.returnReasonDefinitionId = select.value;

        requested.push(line);
      });

      if (!requested.length) {
        setStatus(accountStrings.returnEmpty || '');
        return;
      }

      returnSubmit.disabled = true;
      setStatus(accountStrings.loading || '');

      const result = await accountQuery(RETURN_MUTATION, {
        orderId: activeReturnOrder,
        requestedLineItems: requested,
      });

      returnSubmit.disabled = false;

      const message = mutationMessage(result);
      if (message) {
        setStatus(message);
        return;
      }

      closeReturnForm();
      await load(accountStrings.returnSuccess || '');
    });
  }

  dashboard.querySelectorAll('[data-mm-return-cancel]').forEach((button) => {
    button.addEventListener('click', closeReturnForm);
  });

  if (new URL(window.location.href).searchParams.has('code') || readAccount()) {
    if (signedOut) signedOut.hidden = true;
    setStatus(accountStrings.loading || '');
  }

  const account = await finishSignIn();
  if (!account) {
    showSignedOut();
    setStatus('');
    return;
  }

  await load();
};

// The email form starts the hosted sign-in and comes straight back here with the
// account data, so the address is never typed twice and nobody lands on the
// Shopify-hosted account pages.
document.querySelectorAll('[data-mm-email-form]').forEach((form) => {
  const input = form.querySelector('[data-mm-email-input]');
  const error = form.querySelector('[data-mm-email-error]');
  const status = form.querySelector('[data-mm-email-status]');
  const trigger = form.parentElement.querySelector('[data-mm-shopify-account-trigger]');

  if (!input || !trigger) return;

  form.addEventListener('submit', (event) => {
    event.preventDefault();
    const email = input.value.trim();
    const isValid = input.validity.valid && email.length > 0;

    if (error) error.hidden = isValid;
    input.setAttribute('aria-invalid', String(!isValid));

    if (!isValid) {
      input.focus();
      return;
    }

    if (entryConfig && entryConfig.clientId) {
      input.disabled = true;
      startSignIn(email);
      return;
    }

    if (status) status.textContent = 'Continue in the Shopify secure sign-in panel.';
    trigger.click();
    carryEmailToAccountDialog(form.parentElement.querySelector('shopify-account'), email);
  });

  input.addEventListener('input', () => {
    if (error) error.hidden = true;
    input.removeAttribute('aria-invalid');
  });
});

initMemberAccount();
